// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Form-integrated customer search component module.
 * Provides a React Hook Form compatible wrapper around the CustomerSearch component.
 */
import { CustomerFilter } from '@/customer/api/get-customers';
import { CustomerSearch } from '@/customer/components/customer-search';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

/**
 * Props for the FormCustomerSearch component.
 * Extends React Hook Form's controller props with additional configuration.
 */
interface Props<Values extends FieldValues> extends UseControllerProps<Values> {
  /**
   * Label text displayed above the search input.
   * Provides context for the search field.
   */
  label: string;

  /**
   * React Hook Form control object.
   * Used to integrate the component with form validation and submission.
   */
  control: Control<Values>;

  /**
   * Callback fired when user chooses to create a new customer.
   * @param name - The name entered for the new customer
   */
  onCreate: (name: string) => void;

  /**
   * Optional filter to apply when fetching customers.
   * Used to filter by customer type (isSupplier, isCustomer) or archived status.
   */
  filter?: CustomerFilter;

  /**
   * Optional grouping mode for the customer list.
   * - 'supplier': Groups customers with suppliers first
   * - 'customer': Groups customers with non-suppliers first
   * - undefined: No grouping, only sorted by active/archived status
   */
  groupByMode?: 'supplier' | 'customer';

  /**
   * When true, the clear button is hidden and the value cannot be cleared.
   */
  disableClearable?: boolean;
}

/**
 * A form-integrated version of the CustomerSearch component.
 * Provides seamless integration with React Hook Form for validation and form state management.
 *
 * Features:
 * - Full React Hook Form integration
 * - Automatic error handling
 * - Field state management
 * - Form validation support
 * - Customer creation capability
 *
 * Form Integration:
 * - Uses React Hook Form's useController for field management
 * - Handles undefined values appropriately
 * - Displays validation errors from form state
 * - Maintains form field state
 *
 * @example
 * ```tsx
 * // In a form component:
 * const { control } = useForm({
 *   defaultValues: {
 *     customer: null
 *   }
 * });
 *
 * return (
 *   <FormCustomerSearch
 *     name="customer"
 *     label="Select customer"
 *     control={control}
 *     rules={{ required: 'Customer is required' }}
 *     onCreate={handleCustomerCreate}
 *   />
 * );
 * ```
 *
 * @template Values - The type of the form values object
 */
export function FormCustomerSearch<Values extends FieldValues>(
  props: Props<Values>
) {
  /**
   * Hook into React Hook Form's controller.
   * Provides field state management and validation.
   */
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  /**
   * Handle undefined values by converting to null.
   * This ensures the component remains controlled at all times.
   */
  const value = field.value ?? null;

  return (
    <CustomerSearch
      ref={field.ref}
      name={field.name}
      label={props.label}
      value={value}
      onChange={field.onChange}
      onCreate={props.onCreate}
      filter={props.filter}
      groupByMode={props.groupByMode}
      disableClearable={props.disableClearable}
      error={Boolean(fieldState.error)}
      helperText={fieldState.error?.message}
    />
  );
}
